/**
 * Firm Templates Manager — list + create + edit + delete chart-of-accounts
 * presets that get auto-applied when a firm provisions a new client workspace.
 *
 * Kept as a modal rather than a full page so it's one click away from
 * /app/clients where firms spend their time.
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Layers, Plus, Trash2, Pencil, Loader2, Check, X, Star,
} from "lucide-react";

const GROUPS = ["income", "cogs", "expense", "other"];

const BLANK = { name: "", description: "", categories: [], is_default: false };

export default function FirmTemplatesModal({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {id?, name, description, categories[], is_default}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/firm/templates");
      setTemplates(data || []);
    } catch (e) {
      toast.error("Could not load templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const close = () => {
    if (saving) return;
    setEditing(null);
    onClose();
  };

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (t) => setEditing({ ...t });

  const addRow = () => setEditing((e) => ({ ...e, categories: [...e.categories, { name: "", group: "expense" }] }));
  const setRow = (i, patch) => setEditing((e) => {
    const next = [...e.categories];
    next[i] = { ...next[i], ...patch };
    return { ...e, categories: next };
  });
  const delRow = (i) => setEditing((e) => ({ ...e, categories: e.categories.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Give the template a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description?.trim() || undefined,
        categories: editing.categories.filter((c) => c.name.trim()),
        is_default: !!editing.is_default,
      };
      if (editing.id) {
        await api.patch(`/firm/templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/firm/templates", payload);
        toast.success("Template created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? Existing client workspaces are not affected.")) return;
    try {
      await api.delete(`/firm/templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error("Could not delete template");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="firm-templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Layers size={16} /> Firm templates
          </DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">
            Save a reusable chart-of-accounts preset per industry. Pick one from the Add Client modal to
            pre-seed every new client workspace with the right categories.
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <>
            <div className="flex items-center justify-between pt-2 mb-3">
              <div className="text-xs text-[var(--ll-ink-soft)]" data-testid="templates-count">
                {loading ? "Loading…" : `${templates.length} template${templates.length === 1 ? "" : "s"}`}
              </div>
              <button
                type="button" onClick={startNew}
                data-testid="new-template-btn"
                className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#0b5d9e] flex items-center gap-1.5"
              >
                <Plus size={14} /> New template
              </button>
            </div>

            {!loading && templates.length === 0 && (
              <div className="p-10 text-center border text-sm text-[var(--ll-ink-muted)]" style={{ borderColor: "var(--ll-border)" }} data-testid="templates-empty">
                <Layers size={28} className="mx-auto mb-2 opacity-40" />
                No templates yet. Create one to speed up new-client onboarding.
              </div>
            )}

            <ul className="divide-y border" style={{ borderColor: "var(--ll-border)" }}>
              {templates.map((t) => (
                <li key={t.id} className="p-3 flex items-start gap-3" data-testid={`template-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium truncate">{t.name}</div>
                      {t.is_default && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest px-1.5 py-0.5" style={{ background: "#FEF3C7", color: "#92400E" }}>
                          <Star size={9} fill="#92400E" strokeWidth={0} /> Default
                        </span>
                      )}
                    </div>
                    {t.description && <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5">{t.description}</div>}
                    <div className="text-[11px] text-[var(--ll-ink-muted)] mt-1">
                      {t.categories?.length || 0} categor{(t.categories?.length || 0) === 1 ? "y" : "ies"}
                    </div>
                  </div>
                  <button
                    type="button" onClick={() => startEdit(t)}
                    data-testid={`template-edit-${t.id}`}
                    className="p-1.5 border hover:bg-[var(--ll-mint)]"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button" onClick={() => remove(t.id)}
                    data-testid={`template-delete-${t.id}`}
                    className="p-1.5 border hover:bg-[#FEE2E2] text-[#991B1B]"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {editing && (
          <div className="pt-2 space-y-3" data-testid="template-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1">Template name</label>
                <input
                  value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="template-name" placeholder="Restaurant · standard"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Description <span className="text-[var(--ll-ink-muted)]">(optional)</span></label>
                <input
                  value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  data-testid="template-description" placeholder="Standard chart for restaurants"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={!!editing.is_default}
                onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
                data-testid="template-is-default"
              />
              Make this the firm default (auto-selected in Add Client)
            </label>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Categories</div>
                <button type="button" onClick={addRow} data-testid="add-category-btn" className="text-xs text-[var(--ll-ink)] inline-flex items-center gap-1 hover:underline"><Plus size={11} /> Add category</button>
              </div>
              {editing.categories.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--ll-ink-muted)] border" style={{ borderColor: "var(--ll-border)" }}>
                  No categories yet. Click "Add category" to start.
                </div>
              )}
              <ul className="space-y-1.5">
                {editing.categories.map((c, i) => (
                  <li key={i} className="flex items-center gap-2" data-testid={`category-row-${i}`}>
                    <input
                      value={c.name} onChange={(e) => setRow(i, { name: e.target.value })}
                      placeholder="Category name"
                      data-testid={`category-name-${i}`}
                      className="flex-1 px-2 py-1.5 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                    />
                    <select
                      value={c.group} onChange={(e) => setRow(i, { group: e.target.value })}
                      data-testid={`category-group-${i}`}
                      className="px-2 py-1.5 border text-sm bg-white" style={{ borderColor: "var(--ll-border)" }}
                    >
                      {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <button
                      type="button" onClick={() => delRow(i)}
                      data-testid={`category-delete-${i}`}
                      className="p-1.5 border hover:bg-[#FEE2E2] text-[#991B1B]"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t" style={{ borderColor: "var(--ll-border)" }}>
              <button
                type="button" onClick={() => setEditing(null)} disabled={saving}
                data-testid="template-cancel"
                className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] disabled:opacity-50"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Back
              </button>
              <button
                type="button" onClick={save} disabled={saving}
                data-testid="template-save"
                className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#0b5d9e] disabled:opacity-60 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {editing.id ? "Save changes" : "Create template"}
              </button>
            </div>
          </div>
        )}

        {!editing && (
          <div className="flex items-center justify-end pt-3 border-t mt-3" style={{ borderColor: "var(--ll-border)" }}>
            <button
              type="button" onClick={close}
              data-testid="firm-templates-close"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <X size={13} /> Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
